import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { isLoginPhasePortalLabel } from '../utils/portalScreenshotLabel';
import { normalizePortalScreenshotB64 } from '../utils/portalFrame';
import { getApiBaseUrl } from '../utils/apiBase';
import './WaitingLobby.css';

const API_URL = getApiBaseUrl();
/** 1 = skip lobby dashboard wait (debug only). When unset, portal demos wait until the snapshot looks like a real dashboard. */
const SKIP_LOBBY_PORTAL_WAIT = (process.env.REACT_APP_SKIP_LOBBY_PORTAL_WAIT || '').trim() === '1';
const LOBBY_POLL_MS = Math.max(200, parseInt(process.env.REACT_APP_LOBBY_POLL_MS || '400', 10));
const LOBBY_REDIRECT_DELAY_MS = Math.max(0, parseInt(process.env.REACT_APP_LOBBY_REDIRECT_DELAY_MS || '0', 10));
/** After lobby refresh-screenshot, brief pause before redirect (ms). */
const LOBBY_PORTAL_REFRESH_SETTLE_MS = Math.max(0, parseInt(process.env.REACT_APP_LOBBY_PORTAL_REFRESH_SETTLE_MS || '120', 10));
/**
 * 1 (default): start waiting for dashboard as soon as the session token exists (stay in lobby until preview is good).
 * 0: only start redirect flow after Socket.IO meeting-joined-ready (then still wait for dashboard if portal).
 */
const REDIRECT_ON_TOKEN = (process.env.REACT_APP_LOBBY_REDIRECT_ON_TOKEN || '1').trim() !== '0';

function isDashboardSnapshotReady(payload) {
  if (!payload?.screenshot) return false;
  if (isLoginPhasePortalLabel(payload.label)) return false;
  if (String(payload.label || '').startsWith('Error:')) return false;
  return normalizePortalScreenshotB64(payload.screenshot).length > 80;
}

const STEPS = [
  { step: 'bot-starting',         label: 'Starting agent'    },
  { step: 'livekit-connecting',   label: 'Connecting room'   },
  { step: 'meeting-joined-ready', label: 'Ready'             },
];

const WaitingLobby = ({ roomData, sessionId }) => {
  const [completedSteps, setCompletedSteps] = useState([]);
  const [activeStep,     setActiveStep]     = useState(0);

  const roomDataRef      = useRef(roomData);
  const sessionIdRef     = useRef(sessionId);
  const readyReceivedRef = useRef(false);
  const redirectingRef   = useRef(false);

  /** Portal demos: poll until the live snapshot looks like a loaded dashboard (not login / error / empty). */
  const waitForDashboardReadyUnlessSkipped = async () => {
    if (SKIP_LOBBY_PORTAL_WAIT) return true;
    const portalId = sessionStorage.getItem('demo_portal_id') || '';
    if (!portalId) return true;
    const sid = sessionIdRef.current;
    if (!sid) return true;

    const start = Date.now();
    const MAX_WAIT_MS = 90_000;
    while (Date.now() - start < MAX_WAIT_MS) {
      try {
        const res = await fetch(`${API_URL}/api/portal/snapshot/${sid}`);
        const j = await res.json();
        if (isDashboardSnapshotReady(j)) {
          await fetch(`${API_URL}/api/portal/refresh-screenshot`, {
            method : 'POST',
            headers: { 'Content-Type': 'application/json' },
            body   : JSON.stringify({ sessionId: sid }),
          }).catch(() => {});
          await new Promise(r => setTimeout(r, LOBBY_PORTAL_REFRESH_SETTLE_MS));
          return true;
        }
      } catch (_) { /* ignore */ }
      await new Promise(r => setTimeout(r, LOBBY_POLL_MS));
    }
    return true;
  };

  const doRedirect = async (data) => {
    if (redirectingRef.current) return;
    redirectingRef.current = true;
    await waitForDashboardReadyUnlessSkipped();
    const go = () => {
      const lang     = sessionStorage.getItem('demo_language')  || 'sv-en';
      const portalId = sessionStorage.getItem('demo_portal_id') || '';
      const paramObj = { url: data.url, room: data.room, token: data.token, lang };
      if (portalId) paramObj.portalId = portalId;
      const params = new URLSearchParams(paramObj);
      window.location.href = `/meeting/room?${params.toString()}`;
    };
    if (LOBBY_REDIRECT_DELAY_MS <= 0) go();
    else setTimeout(go, LOBBY_REDIRECT_DELAY_MS);
  };

  useEffect(() => {
    roomDataRef.current  = roomData;
    sessionIdRef.current = sessionId;
    if (roomData?.token && sessionId) {
      if (REDIRECT_ON_TOKEN) doRedirect(roomData);
      else if (readyReceivedRef.current) doRedirect(roomData);
    }
  }, [roomData, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const socket = io(API_URL);

    const joinRoom = () => { if (sessionIdRef.current) socket.emit('join-session', sessionIdRef.current); };
    socket.on('connect', joinRoom);
    joinRoom();

    socket.on('bot-status', (data) => {
      if (data.sessionId && sessionIdRef.current && data.sessionId !== sessionIdRef.current) return;

      if (data.status === 'completed') {
        setCompletedSteps(prev => [...new Set([...prev, data.step])]);
        const idx = STEPS.findIndex(s => s.step === data.step);
        if (idx !== -1) setActiveStep(Math.min(idx + 1, STEPS.length - 1));

        if (data.step === 'meeting-joined-ready') {
          readyReceivedRef.current = true;
          if (roomDataRef.current) doRedirect(roomDataRef.current);
        }
      }
    });

    const fallbackTimer = setTimeout(() => {
      if (!redirectingRef.current && roomDataRef.current) doRedirect(roomDataRef.current);
    }, 40000);

    return () => { socket.disconnect(); clearTimeout(fallbackTimer); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="wl-overlay">
      <div className="wl-card">
        <div className="wl-spinner" />

        <h2 className="wl-title">Starting your demo</h2>
        <p className="wl-subtitle">
          {sessionStorage.getItem('demo_portal_id')
            ? 'Opening your dashboard preview — you’ll join the room when it’s ready.'
            : "You'll be redirected automatically."}
        </p>

        <div className="wl-steps">
          {STEPS.map((s, i) => {
            const done   = completedSteps.includes(s.step);
            const active = !done && i === activeStep;
            return (
              <div key={s.step} className={`wl-step ${done ? 'done' : active ? 'active' : ''}`}>
                <span className="wl-step-dot" />
                <span className="wl-step-label">{s.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WaitingLobby;
